/* Attention items (ADR 0030 §4, CONTEXT.md "Attention"): problems across the
   person's Accounts that need someone to act, most urgent first. AI off on an
   Account with no plan, and having no plan, are choices — never items. */

import type { Account, AccountSubscriptionStatus } from '../api/client';
import { ATTENTION_KINDS, type AttentionKind } from './model';

export const URGENT: ReadonlySet<AttentionKind> = new Set(['killed', 'signed_out', 'billing_suspended']);
/** Three or more Accounts with the same problem show as one group row. */
export const GROUP_MIN = 3;
export const SNOOZE_MS = 24 * 60 * 60 * 1000;

export const accountName = (a: Pick<Account, 'profile_name' | 'username' | 'account_id'>) => a.profile_name || a.username || a.account_id;

/** Created but never connected: no synced profile and not logged in (the sidebar's "Pending"). */
export const isPending = (a: Account) => !a.username && !a.is_logged_in;
export const isSignedOut = (a: Account) => a.is_enabled && !a.is_logged_in && !isPending(a);
/** An active Subscription, or On demand billing. */
export const hasPlan = (s?: AccountSubscriptionStatus) => !!s && (s.active || s.billing_mode === 'turn');

export interface AttentionItem {
  kind: AttentionKind;
  account: Account;
  /** Snooze key: kind:account_id, plus the period end for a plan expiry. */
  key: string;
  urgent: boolean;
  /** plan_expiring only. */
  daysLeft?: number;
}

export function attentionItems(accounts: Account[], subs: Record<string, AccountSubscriptionStatus>, throttled: ReadonlySet<string>): AttentionItem[] {
  const items: AttentionItem[] = [];
  const add = (kind: AttentionKind, account: Account, extra: Partial<AttentionItem> = {}) =>
    items.push({ kind, account, key: `${kind}:${account.account_id}`, urgent: URGENT.has(kind), ...extra });
  for (const a of accounts) {
    const s = subs[a.account_id];
    if (a.kill_switch) add('killed', a);
    if (isSignedOut(a)) add('signed_out', a);
    if (s?.billing_suspended) add('billing_suspended', a);
    if (s?.active && s.days_left != null && s.days_left < 5) {
      add('plan_expiring', a, { key: `plan_expiring:${a.account_id}:${s.current_period_end ?? ''}`, daysLeft: s.days_left });
    }
    if (!a.is_enabled && hasPlan(s) && !s?.billing_suspended && !a.kill_switch) add('ai_paused', a);
    if (throttled.has(a.account_id)) add('throttled', a);
  }
  return items;
}

export type AttentionRow =
  | { type: 'item'; item: AttentionItem }
  | { type: 'group'; kind: AttentionKind; items: AttentionItem[]; urgent: boolean };

/** Kinds in their fixed order; within a kind highest revenue first when known, else by name.
    Three or more of a kind fold into one group row. */
export function attentionRows(items: AttentionItem[], revenueOf?: (accountId: string) => number): AttentionRow[] {
  const rows: AttentionRow[] = [];
  const byName = (x: AttentionItem, y: AttentionItem) => accountName(x.account).localeCompare(accountName(y.account));
  for (const kind of ATTENTION_KINDS) {
    const ofKind = items.filter((i) => i.kind === kind).sort((x, y) => {
      if (revenueOf) {
        const d = revenueOf(y.account.account_id) - revenueOf(x.account.account_id);
        if (d) return d;
      }
      return byName(x, y);
    });
    if (!ofKind.length) continue;
    if (ofKind.length >= GROUP_MIN) rows.push({ type: 'group', kind, items: ofKind, urgent: URGENT.has(kind) });
    else ofKind.forEach((item) => rows.push({ type: 'item', item }));
  }
  return rows;
}

/** Whether a snooze is still running at `now`. */
export const isSnoozed = (snoozes: Record<string, string>, key: string, now: number) => {
  const until = snoozes[key];
  return !!until && Date.parse(until) > now;
};

/** The snoozes with `keys` added for 24 hours and any expired ones dropped. */
export function snooze(snoozes: Record<string, string>, keys: string[], now: number): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, until] of Object.entries(snoozes)) if (Date.parse(until) > now) out[k] = until;
  const until = new Date(now + SNOOZE_MS).toISOString();
  keys.forEach((k) => { out[k] = until; });
  return out;
}

export function unsnooze(snoozes: Record<string, string>, keys: string[]): Record<string, string> {
  const out = { ...snoozes };
  keys.forEach((k) => delete out[k]);
  return out;
}

/* ── Account status (Account list) ─────────────────────────────── */

export type AccountStatus = 'killed' | 'signed_out' | 'billing_suspended' | 'throttled' | 'live' | 'pending' | 'off';

/** One status per Account, first match. */
export function accountStatus(a: Account, s: AccountSubscriptionStatus | undefined, throttled: ReadonlySet<string>): AccountStatus {
  if (a.kill_switch) return 'killed';
  if (isSignedOut(a)) return 'signed_out';
  if (s?.billing_suspended) return 'billing_suspended';
  if (throttled.has(a.account_id)) return 'throttled';
  if (a.is_enabled && a.is_logged_in) return 'live';
  if (isPending(a)) return 'pending';
  return 'off';
}
